import moment from "moment";
import { MEMBER_API_BASE_URL } from "../../../helper/axiosHelper";

export const AVATAR_COLORS = [
    "#0f6cbd",
    "#8764b8",
    "#038387",
    "#c239b3",
    "#ca5010",
    "#0e700e",
    "#69797e",
];

export const PER_PAGE = 10;

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const API_DATE_FORMAT = "MM-DD-YYYY HH:mm";

export const senderName = (email) => {
    const local = String(email || "").split("@")[0];
    if (!local) return email || "Unknown";
    const words = local.replace(/[._-]+/g, " ").trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
};

export const attachmentUrl = (path) =>
    `${MEMBER_API_BASE_URL}/${String(path || "")
        .split("/")
        .map(encodeURIComponent)
        .join("/")}`;

export const downloadAttachment = async (url, filename) => {
    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(String(response.status));
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = objectUrl;
        link.download = filename || "attachment";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
        window.open(url, "_blank", "noopener");
    }
};

export const fileKind = (filename) => {
    const ext = String(filename || "").split(".").pop().toLowerCase();
    if (ext === "pdf") return { cls: "pdf", label: "PDF", type: "PDF Document" };
    if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext))
        return { cls: "img", label: ext.toUpperCase(), type: `${ext.toUpperCase()} Image` };
    if (["xls", "xlsx", "csv"].includes(ext))
        return { cls: "xls", label: ext.toUpperCase(), type: "Spreadsheet" };
    if (["doc", "docx", "rtf"].includes(ext))
        return { cls: "doc", label: ext.toUpperCase(), type: "Document" };
    const label = (ext || "file").slice(0, 4).toUpperCase();
    return { cls: "", label: label, type: `${label} File` };
};

export const initials = (name) =>
    (name || "")
        .split(" ")
        .filter(Boolean)
        .map((word) => word[0])
        .join("")
        .slice(0, 2)
        .toUpperCase() || "—";

export const shortStamp = (value) => {
    const date = moment(value, API_DATE_FORMAT);
    if (!date.isValid()) return value || "";
    if (date.isSame(moment(), "day")) return date.format("h:mm A");
    if (date.isSame(moment().subtract(1, "day"), "day")) return "Yesterday";
    if (date.isAfter(moment().subtract(6, "day"))) return date.format("ddd");
    return date.format("MMM D");
};

export const longStamp = (value) => {
    const date = moment(value, API_DATE_FORMAT);
    return date.isValid() ? date.format("ddd, DD MMM YYYY, h:mm A") : value || "";
};

export const statusSuffix = (status) => {
    const value = (status || "").toLowerCase();
    if (value.includes("open") || value.includes("new")) return "open";
    if (value.includes("progress") || value.includes("hold") || value.includes("wait"))
        return "prog";
    if (value.includes("resolv") || value.includes("clos") || value.includes("complet"))
        return "res";
    return "neu";
};

export const prioritySuffix = (priority) => {
    const value = (priority || "").toLowerCase();
    if (value.includes("high") || value.includes("urgent")) return "high";
    if (value.includes("medium") || value.includes("normal")) return "med";
    if (value.includes("low")) return "low";
    return "neu";
};

export const storeWithId = (storeName, hraMemberId) => {
    if (!storeName) return "";
    if (!hraMemberId) return storeName;
    return storeName.includes("HRA") ? storeName : `${storeName} - ${hraMemberId}`;
};

export const composeSubject = (category, storeFull, vendor) =>
    `${category || ""}: ${storeFull ? `${storeFull} &` : ""}${vendor || ""}`.trim();

/* "Created by" is the ticket owner in the CRM, stored on the ticket row as
   manager_territory. The sync builds it from first and last name, so a
   missing last name arrives as "Katrina null" - the "null" is dropped. */
const createdByOf = (owner) =>
    String(owner || "")
        .split(" ")
        .filter((part) => part && part !== "null" && part !== "undefined")
        .join(" ");

export const toView = (ticket, index) => {
    const member = ticket?.member || {};
    const person =
        `${member.firstName || ""} ${member.lastName || ""}`.trim() ||
        ticket?.ownerEmail ||
        "Unknown";
    const storeName = ticket?.store || member.storeNameRegular || "";
    const storeFull = storeWithId(storeName, member.hraMemberId);
    const address = [
        member.mailingStreetAddress,
        member.city,
        member.state,
        member.mailingZip,
    ]
        .filter(Boolean)
        .join(", ");

    return {
        key: String(ticket?.ID ?? ticket?.ticketId ?? index),
        id: ticket?.ID ?? null,
        no: ticket?.ticketName || `#${ticket?.ticketId || ""}`,
        /* which vendor the ticket belongs to, resolved by the API from the
           caller's accessible vendors - only worth showing when they have
           more than one, see SupportTickets.js */
        vendorName: ticket?.vendorName || "",
        subj: composeSubject(ticket?.category, storeFull, ticket?.vendor),
        category: ticket?.category || "",
        person: person,
        createdBy: createdByOf(ticket?.managerTerritory),
        company: storeName,
        storeFull: storeFull,
        /* the bare store name for Store Details, where the HRA member id has
           its own cell - Zoho's store names carry it as "Petro Foodmart -
           HRA9812", so the trailing id is cut off */
        storeOnly: storeName.replace(/\s*-?\s*HRA\d+\s*$/i, "").trim(),
        hraMemberId: member.hraMemberId || "",
        phone: member.phoneNumberMobileNumber || "",
        address: address,
        /* the store's own field manager and membership, read from the member
           record - not the ticket's manager_territory, which is the CRM
           ticket owner */
        storeTerritory: member.managerTerritory || "",
        membershipStatus: member.membershipStatus || "",
        from: storeName ? `${person} (${storeName})` : person,
        prev: ticket?.details || "",
        status: ticket?.status || "",
        time: shortStamp(ticket?.createdDate),
        createdDate: ticket?.createdDate,
        updatedDate: ticket?.updatedDate,
        modifierName: ticket?.modifierName,
        priority: ticket?.priority,
        vendorName: ticket?.vendor,
        email: member.emailAddress || ticket?.ownerEmail || "",
        color: AVATAR_COLORS[index % AVATAR_COLORS.length],
    };
};

/* every react-select on the page portals its menu to the body, otherwise the
   list panel's own scrolling clips it */
export const SELECT_PROPS = {
    classNamePrefix: "vp-tk-sel",
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    styles: { menuPortal: (base) => ({ ...base, zIndex: 1100 }) },
    isClearable: true,
};

export const optionOf = (options, value) =>
    (options || []).find((option) => option.value === value) || null;
